import React, { useContext, useEffect, useState } from 'react';

// Styles & Assets
import styles from './PatientInfoGrid.module.css';

// Services

import { getAllergiesPatient, getDaysStayTime, getDiagnosisPatient } from '../../../services/getPatientAtribute';

import PreconfigPatientButton from '../PreconfigPatientButton/PreconfigPatientButton';

// Context
import ScoresContext from '../../../context/ScoresContext';

// Hooks
import { useTranslation } from 'react-i18next';

// Components
import PatientInfoText from '../../PatientsPage/PatientInfoText/PatientInfoText';
import VIG from '../../Helpers/VIG/VIG';

// Interfaces
import { Case, IScoreCircle, Patient } from '../../../interfaces/Patient';
import { getEnvironmentApiUrl } from '../../../services/General';
import { PhysiologicalMonitoring } from '../../../interfaces/PhysiologicalMonitoring';
import useHttp from '../../../hooks/useHttp';
import { getCaseTimeAdmit } from '../../../services/Patient';
import ScoreCircle from '../../UI/ScoreCircle/ScoreCircle';
interface Props {
    patient: Patient;
    caso: Case;
    scoreCV: IScoreCircle;
    refreshPatientSilently: () => Promise<void>;
}

// Constants
const summaryProperties = ['diagnosis', 'allergies', 'bed', 'nhc', 'cip', 'news', 'preconfiguration', 'days_stay_time', 'timeAdmit', 'telecare', 'municipal_sad', 'vig'] as const;
type IPropertiesSummary = typeof summaryProperties[number];

const DEFAULT_PROPERTIES: IPropertiesSummary[] = ['bed', 'diagnosis', 'news', 'nhc', 'cip', 'preconfiguration', 'allergies'];
const MAX_PROPERTIES = 8;
const PROPETIES = window.config.PATIENT_VIEW_SUMMARY_PROPERTIES ?? DEFAULT_PROPERTIES;
const propertiesToShow = (PROPETIES as string[])
    .filter((p): p is IPropertiesSummary => summaryProperties.includes(p as IPropertiesSummary))
    .slice(0, MAX_PROPERTIES);

const PatientInfoGrid: React.FC<Props> = ({
    patient,
    caso,
    scoreCV,
    refreshPatientSilently
}) => {
    const { t } = useTranslation();
    const { sendRequest: getLastSadValues } = useHttp<PhysiologicalMonitoring[]>();
    const { scores } = useContext(ScoresContext);

    const scoreNews = scores?.find(sc => sc.id === 1);

    const [vig, setVig] = useState<number | null>(null)
    const [telecare, setTelecare] = useState<boolean | null>(null)
    const [municipal, setMunicipal] = useState<string | null>(null)

    useEffect(() => {
        getLastSadValues({
            url: getEnvironmentApiUrl() + 'physiological_monitoring/get_last_before',
            method: 'POST',
            body: {
                threshold_seconds: 0,
                sheet_param_ids: ['VIG', 'SAD_MUNICIPAL', 'TELEASISTENCIA'],
                date: new Date().toISOString(),
                case_id: caso.id,
            }
        }, (data) => {
            if (data.length === 0) return;
            const vigPhys = data.find(phys => phys.sheet_parameter_id === 'VIG');
            const municipalPhys = data.find(phys => phys.sheet_parameter_id === 'SAD_MUNICIPAL');
            const telecarePhys = data.find(phys => phys.sheet_parameter_id === 'TELEASISTENCIA');
            if (vigPhys) setVig(vigPhys.value ?? null);
            if (municipalPhys) setMunicipal(municipalPhys.sheet_parameter_option_id ?? null);
            if (telecarePhys) setTelecare(
                Number(telecarePhys.value) === 1 ?
                    true : Number(telecarePhys.value) === 0 ? false : null
            )
        })
    }, [getLastSadValues, caso.id]);

    const yesOrNo = (value: boolean | null | undefined) =>
        value === true ? t('yes') : value === false ? t('no') : '';



    // Props config map
    const propertyRenderMap: Record<IPropertiesSummary, (patient: Patient) => React.ReactNode> = {
        bed: (p) => (
            <PatientInfoText
                label={t('bed')}
                element={p.beds && p.beds.length > 0 && p.beds[0].name ? p.beds[0].name : '—'}
            />
        ),
        timeAdmit: () => (
            <PatientInfoText
                label={t('time_admit')}
                element={getCaseTimeAdmit(caso)}
            />
        ),
        diagnosis: (p) => (
            <PatientInfoText label={t('diagnosis')} element={getDiagnosisPatient(p)} />
        ),
        news: () => (
            <PatientInfoText
                label={t('NEWS')}
                element={
                    scoreNews ? (
                        <ScoreCircle
                            rules={scoreNews.score_rules}
                            lastScore={scoreCV.lastScore}
                            secondLastScore={scoreCV.secondLastScore}
                        />
                    ) : '-'
                }
            />
        ),
        nhc: (p) => (
            <PatientInfoText label={t('NHC')} element={p.mrn || ''} />
        ),
        cip: (p) => (
            <PatientInfoText label={t('CIP')} element={p.pid_1 || ''} />
        ),
        preconfiguration: (p) => (
            <PatientInfoText
                label={t('preconfiguration')}
                element={
                    <PreconfigPatientButton
                        patient={p}
                        refreshPatientSilently={refreshPatientSilently}
                    />
                }
            />
        ),
        allergies: (p) => (
            <PatientInfoText label={t('allergies')} element={getAllergiesPatient(p)} />
        ),
        days_stay_time: (p) => (
            <PatientInfoText label={t('stay_time')} element={getDaysStayTime(p) + ' ' + t('days')} />
        ),
        telecare: () => (
            <PatientInfoText label={t('telecare')} element={yesOrNo(telecare)} />
        ),
        municipal_sad: () => (
            <PatientInfoText label={t('municipal_sad')} element={t(municipal ?? '-') as string} />
        ),
        vig: () => (
            <div className={styles.vig}>
                <span><strong>VIG:</strong></span>
                <VIG value={vig} />
            </div>
        ),
    };

    return (
        <div className={styles.generalInfoBottom}>
            {propertiesToShow.map(property => (
                <React.Fragment key={property}>
                    {propertyRenderMap[property](patient)}
                </React.Fragment>
            ))}
        </div>
    );
};

export default PatientInfoGrid;